/* eslint-disable no-restricted-globals */
/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import gsap from 'gsap';
import classNames from 'classnames';
import normalizeWheel from 'normalize-wheel';
import React, {
  createContext,
  useState,
  useContext,
  useLayoutEffect,
  useEffect,
  useRef,
} from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import styles from './Region.module.scss';

const diff = (a, b) => (a > b ? a - b : b - a);

const lerp = (current, target, multiplier) =>
  (1 - multiplier) * current + multiplier * target;

export const REGION_STATE = {
  active: 'Region/State/Active',
  inactive: 'Region/State/Inactive',
};

const initialState = {
  regions: [],
  history: [],
};

export const RegionsContext = createContext();

export const RegionsStore = ({ children }) => {
  const [state, setState] = useState(initialState);

  const register = region => {
    setState(previous => {
      const regions = [
        ...previous.regions,
        { ...region, state: REGION_STATE.inactive },
      ];
      return { ...previous, regions };
    });
  };

  const remove = ({ id }) => {
    setState(previous => {
      const regions = previous.regions.filter(region => region.id !== id);
      return { ...previous, regions };
    });
  };

  const activate = ({ id, element }) => {
    setState(previous => {
      const history = [...previous.history, id];
      const regions = [...previous.regions].map(region => ({
        ...region,
        element: element && id === region.id ? element : region.element,
        state: id === region.id ? REGION_STATE.active : REGION_STATE.inactive,
      }));
      return { ...previous, regions, history };
    });
  };

  const close = ({ id, clear = false }) => {
    setState(previous => {
      // If not active return
      if (previous.history[previous.history.length - 1] !== id) {
        return previous;
      }

      // Remove last from history
      const history = [...previous.history];
      history.pop();

      const regions = [...previous.regions].map(region => ({
        ...region,
        state:
          history[history.length - 1] === region.id
            ? REGION_STATE.active
            : REGION_STATE.inactive,
        element: clear && id === region.id ? null : region.element,
        position: clear && id === region.id ? 0 : region.position,
      }));

      return { ...previous, regions, history };
    });
  };

  return (
    <RegionsContext.Provider
      value={{ ...state, register, remove, activate, close }}
    >
      {children}
    </RegionsContext.Provider>
  );
};

export const Region = ({
  children,
  name,
  state,
  damping = 0.0,
  scrollSpeed = 25,
  className,
}) => {
  const { register, activate, remove, regions } = useContext(RegionsContext);
  const region = regions.find(({ id }) => id === name) || {};

  const observer = useRef(null);
  const container = useRef(null);
  const content = useRef(null);
  const scrollWheelPos = useRef(0);
  const isScrolling = useRef(false);

  /**
   *
   *
   */
  const setScrollbarPosition = () => {
    region.scrollPosition = region.position;
    window.scrollTo(0, region.position);
  };

  const setContentHeight = () => {
    if (content.current !== undefined && content.current !== null) {
      const { height } = content.current.getBoundingClientRect();
      document.body.style.height = `${height}px`;
    }
  };

  const updateUiStyles = () => {
    if (region.state === REGION_STATE.inactive) {
      if (container.current) container.current.style.position = 'fixed';
      if (content.current) {
        content.current.style.transform = `matrix(1, 0, 0, 1, 0, ${-region.scrollPosition})`;
      }
    } else {
      if (container.current) container.current.style.position = 'relative';
      if (content.current) {
        content.current.style.transform = null;
      }
    }
  };

  const setContentTransform = () => {
    if (!isScrolling.current) return;
    // Only when damping, lerp position
    if (damping) {
      document.documentElement.scrollTop = region.position;
      region.position =
        Math.round(
          lerp(region.position, region.scrollPosition, damping) * 100
        ) / 100;

      if (diff(region.position, region.scrollPosition) < 1) {
        isScrolling.current = false;
      }
    }
  };

  const handleScroll = () => {
    // This is triggered by:
    // - dragging the scrollbar
    // - tabbing through the website
    // - Dragging of touch devices;
    if (!isScrolling.current) {
      region.position = window.scrollY;
      region.scrollPosition = window.scrollY;
      scrollWheelPos.current = window.scrollY;
    }
  };

  const handleMouseWheelScroll = e => {
    e.preventDefault();

    const normalized = normalizeWheel(e);
    scrollWheelPos.current += normalized.pixelY * 0.6;

    // limit scrolling;
    scrollWheelPos.current = Math.max(
      0,
      Math.min(
        scrollWheelPos.current,
        document.body.scrollHeight - document.documentElement.clientHeight
      )
    );

    region.scrollPosition = scrollWheelPos.current;
    if (!isScrolling.current) isScrolling.current = true;
  };

  const handleResize = () => {
    setContentHeight();
    setContentTransform();
  };

  const handleTick = () => {
    setContentTransform();
  };

  const setupEventListeners = () => {
    observer.current = new ResizeObserver(handleResize);
    observer.current.observe(content.current);

    document.addEventListener('scroll', handleScroll);
    document.addEventListener('wheel', handleMouseWheelScroll, {
      passive: false,
    });

    gsap.ticker.add(handleTick);
  };

  const removeEventListeners = () => {
    gsap.ticker.remove(handleTick);
    document.removeEventListener('scroll', handleScroll);
    document.removeEventListener('wheel', handleMouseWheelScroll);

    if (observer.current) {
      observer.current.disconnect();
    }
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);

    /**
     * Disable scroll restoration
     */
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    /**
     *
     *  Register region
     */
    register({
      id: name,
      element: children,
      scrollPosition: window.scrollY,
      position: window.scrollY,
    });

    if (state === REGION_STATE.active) {
      /**
       *
       *  If state prop active,
       *  activate region
       */
      activate({ id: name });
    }

    return () => {
      /**
       *
       *  Remove on unmount
       */
      remove({ id: name });
    };
  }, []);

  /**
   *
   */
  useEffect(() => {
    if (region.state === REGION_STATE.active) {
      setContentHeight();
      setScrollbarPosition();
      setContentTransform();
      setupEventListeners();
    }

    updateUiStyles();

    return () => {
      removeEventListeners();
    };
  }, [region]);

  return region.element ? (
    <div className={classNames(styles.container, className)} ref={container}>
      <div className={classNames(styles.content)} ref={content}>
        {region.element}
      </div>
    </div>
  ) : null;
};
